import {
  getSelectedIntegrations,
  type IntegrationName,
  type OrgOnboardingStatus,
  type OrgPlanName,
} from "@tellescope/tanstack-query";
import { getPlanIntegrationConfigs } from "../constants";
import { INTEGRATION_SETUP_STEPS } from "./registry";
import type { IntegrationStepDefinition, StepOutcome } from "./types";

/** The status-record key under which a step's outcome is persisted. */
export const stepStatusKey = (integration: IntegrationName, stepKey: string): string => `${integration}:${stepKey}`;

/** A step within the flattened, selected-integration setup sequence. */
export interface FlowStep {
  integration: IntegrationName;
  step: IntegrationStepDefinition;
  /** Position of this step across the entire flattened flow (0-based). */
  index: number;
}

export interface SetupFlow {
  /** All steps for selected integrations, in plan order. */
  steps: FlowStep[];
  /** The step the user should currently be on, or `undefined` when the flow is done. */
  currentStep: FlowStep | undefined;
  /** Integrations active in this flow, in plan order (plan-required ones plus those the user selected). */
  selectedIntegrations: IntegrationName[];
  /** `true` when no integration has been selected yet (show the selection step). */
  needsIntegrationSelection: boolean;
  /** `true` when there are steps and every one of them has been resolved (completed or skipped). */
  isFlowComplete: boolean;
}

/** `true` when the plan offers integrations and every one of them is required. */
const allPlanIntegrationsRequired = (plan: OrgPlanName): boolean => {
  const configs = getPlanIntegrationConfigs(plan);
  return configs.length > 0 && configs.every((config) => config.required === true);
};

/**
 * Selected integrations, ordered by the plan's integration config order. When
 * every integration the plan offers is required there is nothing to choose, so
 * all of them are part of the flow regardless of what the status record holds;
 * otherwise only the integrations present on the record are included.
 */
const planOrderedSelectedIntegrations = (plan: OrgPlanName, status: OrgOnboardingStatus): IntegrationName[] => {
  const configs = getPlanIntegrationConfigs(plan);
  if (allPlanIntegrationsRequired(plan)) {
    return configs.map(({ name }) => name);
  }

  const selected = new Set(getSelectedIntegrations(status));
  return configs.reduce<IntegrationName[]>((names, { name }) => {
    if (selected.has(name)) names.push(name);
    return names;
  }, []);
};

/** The flattened, selected-integration setup sequence in plan order. */
const flowStepsFor = (plan: OrgPlanName, status: OrgOnboardingStatus): FlowStep[] =>
  planOrderedSelectedIntegrations(plan, status).reduce<FlowStep[]>((acc, integration) => {
    INTEGRATION_SETUP_STEPS[integration].forEach((step) => acc.push({ integration, step, index: acc.length }));
    return acc;
  }, []);

/** `true` when the step is the last in its integration's setup sequence. */
const isLastStepOf = (integration: IntegrationName, stepKey: string): boolean => {
  const integrationSteps = INTEGRATION_SETUP_STEPS[integration];
  return integrationSteps[integrationSteps.length - 1]?.key === stepKey;
};

/**
 * Derives the current state of the setup flow from the plan and the parsed
 * onboarding status — pure and deterministic.
 *
 * Selection is needed when no integration keys are present — unless the plan's
 * integrations are all required, in which case there is nothing to choose and
 * the flow starts directly at the first step. The current step is the first
 * flattened step whose `${integration}:${stepKey}` status key is absent (i.e. not
 * yet completed or skipped); the flow is complete once every step is resolved.
 *
 * @param plan The org's plan.
 * @param status The parsed onboarding status record.
 * @returns The flattened `steps`, the `currentStep` (or `undefined`),
 *   `needsIntegrationSelection`, and `isFlowComplete`.
 */
export const deriveSetupFlow = ({ plan, status }: { plan: OrgPlanName; status: OrgOnboardingStatus }): SetupFlow => {
  const orderedSelected = planOrderedSelectedIntegrations(plan, status);
  const steps = flowStepsFor(plan, status);

  const currentStep = steps.find(
    (flowStep) => status?.[stepStatusKey(flowStep.integration, flowStep.step.key)] === undefined
  );

  return {
    steps,
    currentStep,
    selectedIntegrations: orderedSelected,
    needsIntegrationSelection: orderedSelected.length === 0,
    isFlowComplete: steps.length > 0 && currentStep === undefined,
  };
};

/**
 * Computes the status patch to merge after a step is resolved. Records the
 * step's outcome under its `${integration}:${stepKey}` key; when the step is the
 * integration's last step and was *completed* (not skipped), additionally marks
 * the integration `true` so feature-gating treats its setup as ready. Whole-flow
 * completion is derived at runtime (see {@link deriveSetupFlow}), not persisted.
 */
export const getStatusAfterStep = ({
  integration,
  stepKey,
  outcome,
}: {
  integration: IntegrationName;
  stepKey: string;
  outcome: StepOutcome;
}): Partial<OrgOnboardingStatus> => {
  const patch: Partial<OrgOnboardingStatus> = { [stepStatusKey(integration, stepKey)]: outcome };
  if (outcome === "complete" && isLastStepOf(integration, stepKey)) {
    patch[integration] = true;
  }
  return patch;
};

/**
 * Computes the full status record after back-navigation from the current step.
 * Removes the previous step's status key (and its `[integration]: true` readiness
 * marker, if that step was an integration's last) so the previous step becomes
 * current again — key removal requires a full overwrite, so this returns the
 * whole next record for `replaceStatus`. Returns `"selection"` when the current
 * step is the very first step and the plan lets the user choose integrations; for
 * an all-required plan there is no selection step, so the record is unchanged.
 */
export const getStatusAfterBack = ({
  plan,
  status,
  integration,
  stepKey,
}: {
  plan: OrgPlanName;
  status: OrgOnboardingStatus;
  integration: IntegrationName;
  stepKey: string;
}): OrgOnboardingStatus | "selection" => {
  const steps = flowStepsFor(plan, status);
  const currentIndex = steps.findIndex((step) => step.integration === integration && step.step.key === stepKey);
  const previous = currentIndex > 0 ? steps[currentIndex - 1] : undefined;

  if (!previous) return allPlanIntegrationsRequired(plan) ? status : "selection";

  const keysToRemove = new Set([stepStatusKey(previous.integration, previous.step.key)]);
  if (isLastStepOf(previous.integration, previous.step.key)) keysToRemove.add(previous.integration);

  return Object.entries(status ?? {}).reduce<Record<string, string | boolean>>((acc, [key, value]) => {
    if (!keysToRemove.has(key)) acc[key] = value;
    return acc;
  }, {});
};

/**
 * Computes the full status record after an integration-selection change.
 * Newly-selected integrations start at `false`, still-selected integrations keep
 * their progress (their step-status keys and readiness marker), and deselected
 * integrations are dropped. Intended for `replaceStatus` (key removal requires
 * overwrite).
 */
export const getStatusForSelection = ({
  selected,
  status,
}: {
  selected: IntegrationName[];
  status: OrgOnboardingStatus;
}): OrgOnboardingStatus => {
  const selectedSet = new Set<string>(selected);
  const next = Object.entries(status ?? {}).reduce<Record<string, string | boolean>>((acc, [key, value]) => {
    const integration = key.split(":")[0];
    if (integration && selectedSet.has(integration)) acc[key] = value;
    return acc;
  }, {});
  selected.forEach((integration) => {
    next[integration] = next[integration] ?? false;
  });
  return next;
};
